import type { ChartDataSource } from "@/types/chartDataSource";
import type { Component } from "@/types";
import {
  getRecordArray,
  isRecord,
  isLegacyChartDataSource,
  parseComponentDataBinding,
} from "@/types/componentDataBinding";
import type {
  CatalogSourceReconnectReason,
  ComponentDataWriteOperation,
  ComponentDataBinding,
  ComponentDataBindingWriteTarget,
  ComponentDataBindingSource,
  DataSourceRef,
  FederatedViewSourceError,
} from "@/types/componentDataBinding";

export type ComponentDataResolverRefetch = () => Promise<void>;

export type ComponentDataResolverRowSource =
  | "binding"
  | "legacy-query-template"
  | "sampleData"
  | "properties.data"
  | "properties.events"
  | "empty";

export type ComponentDataResolverSourceKind =
  | "binding-static"
  | "binding-schema"
  | "binding-query-template"
  | "binding-saved-view"
  | "binding-federated-view"
  | "legacy-query-template"
  | "legacy-data-source"
  | "legacy-static"
  | "empty";

export type ComponentDataResolverIssueCode =
  | "binding-manual-review-required"
  | "invalid-binding"
  | "invalid-runtime-rows"
  | "missing-legacy-query-template-source"
  | "malformed-events"
  | "malformed-properties-data"
  | "malformed-sample-data";

export type ComponentDataResolverReconnectCode =
  | CatalogSourceReconnectReason
  | "manual-review-required"
  | "select-target";

export interface ComponentDataResolverIssue {
  code: ComponentDataResolverIssueCode;
  message: string;
}

export interface ComponentDataResolverError {
  code: ComponentDataResolverIssueCode | CatalogSourceReconnectReason;
  message: string;
}

export interface ComponentDataResolverReconnectState {
  code: ComponentDataResolverReconnectCode;
  message: string;
}

export interface ComponentDataResolverSourceMetadata {
  kind: ComponentDataResolverSourceKind;
  rowSource: ComponentDataResolverRowSource;
  binding: ComponentDataBinding | null;
  bindingSource: ComponentDataBindingSource | null;
  legacyDataSource: ChartDataSource | null;
  legacyQueryTemplateId: string | null;
  legacyQueryTemplateOrganizationId: string | null;
  sourceErrors: FederatedViewSourceError[];
  issues: ComponentDataResolverIssue[];
}

export interface ComponentDataResolverDiagnostics {
  issues: ComponentDataResolverIssue[];
}

export interface ComponentDataResolverRuntimeState {
  rows?: unknown;
  loading?: boolean;
  error?: ComponentDataResolverError | string | null;
  reconnect?: ComponentDataResolverReconnectState | null;
  preserveRowsOnSourceError?: boolean;
  sourceErrors?: FederatedViewSourceError[];
  refetch?: ComponentDataResolverRefetch;
}

export interface ComponentDataResolverWriteTargetRuntimeState {
  operations?: Partial<Record<ComponentDataWriteOperation, boolean>>;
  requestingOrganizationId?: string | null;
  loading?: boolean;
  error?: ComponentDataResolverError | string | null;
  reconnect?: ComponentDataResolverReconnectState | null;
}

export type ComponentDataWriteTargetPermissions = Record<
  ComponentDataWriteOperation,
  boolean
>;

export interface ComponentDataResolverWriteTargetMetadata {
  writeTarget: ComponentDataBindingWriteTarget | null;
  source: DataSourceRef | null;
  requestingOrganizationId: string | null;
  operations: ComponentDataWriteTargetPermissions;
  loading: boolean;
  error: ComponentDataResolverError | null;
  reconnect: ComponentDataResolverReconnectState | null;
}

export interface ComponentDataResolverOptions {
  bindingState?: ComponentDataResolverRuntimeState;
  writeTargetState?: ComponentDataResolverWriteTargetRuntimeState;
  legacyQueryTemplateState?: ComponentDataResolverRuntimeState;
  refetch?: ComponentDataResolverRefetch;
}

export interface ComponentDataResolverResult {
  rows: Record<string, unknown>[];
  loading: boolean;
  error: ComponentDataResolverError | null;
  reconnect: ComponentDataResolverReconnectState | null;
  writeTarget: ComponentDataResolverWriteTargetMetadata;
  source: ComponentDataResolverSourceMetadata;
  diagnostics: ComponentDataResolverDiagnostics;
  refetch: ComponentDataResolverRefetch;
}

interface LegacyStaticResolution {
  rows: Record<string, unknown>[];
  rowSource: ComponentDataResolverRowSource;
  issues: ComponentDataResolverIssue[];
  hasGuardedRows: boolean;
}

const defaultRefetch: ComponentDataResolverRefetch = async () => {};
const NO_WRITE_TARGET_PERMISSIONS: ComponentDataWriteTargetPermissions = {
  create: false,
  update: false,
  delete: false,
};
const SELECT_WRITE_TARGET_MESSAGE =
  "Select a write target before creating, updating, or deleting records.";
const RESOLVE_WRITE_AUTHORITY_MESSAGE =
  "Resolved source write authority is required before writing.";

function hasOwnProperty(
  value: Record<string, unknown>,
  propertyName: string,
): boolean {
  return Object.prototype.hasOwnProperty.call(value, propertyName);
}

function getTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function getLegacyQueryTemplateId(component: Component): string | null {
  return getTrimmedString(component.properties.queryTemplateId);
}

export function getComponentLegacyQueryTemplateId(
  component: Component,
): string | null {
  return getLegacyQueryTemplateId(component);
}

function getLegacyDataSource(component: Component): ChartDataSource | null {
  const dataSource = component.properties.dataSource;
  return isLegacyChartDataSource(dataSource) ? dataSource : null;
}

export function getComponentLegacyDataSource(
  component: Component,
): ChartDataSource | null {
  return getLegacyDataSource(component);
}

function getPersistedLegacyQueryOrganizationId(
  component: Component,
): string | null {
  const dataSource = component.properties.dataSource;
  if (!isRecord(dataSource)) {
    return null;
  }

  const dataSourceOrganizationId = getTrimmedString(dataSource.organizationId);
  if (dataSourceOrganizationId !== null) {
    return dataSourceOrganizationId;
  }

  const queryDefinition = dataSource.queryDefinition;
  if (!isRecord(queryDefinition)) {
    return null;
  }

  return getTrimmedString(queryDefinition.organizationId);
}

export function getComponentLegacyQueryOrganizationId(
  component: Component,
): string | null {
  return getPersistedLegacyQueryOrganizationId(component);
}

export function createLegacyQueryTemplateDataSource(
  component: Component,
): ChartDataSource | undefined {
  const legacyDataSource = getComponentLegacyDataSource(component);
  const legacyQueryTemplateId = getComponentLegacyQueryTemplateId(component);
  const organizationId = getComponentLegacyQueryOrganizationId(component);

  if (
    legacyDataSource !== null &&
    (legacyDataSource.type === "query-template" ||
      legacyDataSource.type === "timestamp-aggregate" ||
      legacyDataSource.type === "workflow" ||
      legacyDataSource.type === "composite-query")
  ) {
    return legacyDataSource;
  }

  if (legacyQueryTemplateId !== null && organizationId !== null) {
    return {
      type: "query-template",
      organizationId,
    };
  }

  return undefined;
}

function isLegacyQueryTemplateActive(
  legacyDataSource: ChartDataSource | null,
  legacyQueryTemplateId: string | null,
): boolean {
  return (
    legacyDataSource?.type === "query-template" ||
    legacyQueryTemplateId !== null
  );
}

function getLegacyStaticResolution(
  properties: Record<string, unknown>,
): LegacyStaticResolution {
  const issues: ComponentDataResolverIssue[] = [];
  const sampleData = getRecordArray(properties.sampleData);
  const componentData = getRecordArray(properties.data);
  const eventData = getRecordArray(properties.events);
  const hasMalformedSampleData =
    properties.sampleData !== undefined && sampleData === undefined;
  const hasMalformedComponentData =
    properties.data !== undefined && componentData === undefined;
  const hasMalformedEventData =
    properties.events !== undefined && eventData === undefined;

  if (hasMalformedSampleData) {
    issues.push({
      code: "malformed-sample-data",
      message:
        "Legacy sampleData was ignored because it is not an array of records.",
    });
  }

  if (hasMalformedComponentData) {
    issues.push({
      code: "malformed-properties-data",
      message:
        "Legacy properties.data was ignored because it is not an array of records.",
    });
  }

  if (hasMalformedEventData) {
    issues.push({
      code: "malformed-events",
      message:
        "Legacy events were ignored because they are not an array of records.",
    });
  }

  if (sampleData !== undefined) {
    return {
      rows: sampleData,
      rowSource: "sampleData",
      issues,
      hasGuardedRows: sampleData.length > 0,
    };
  }

  if (componentData !== undefined) {
    return {
      rows: componentData,
      rowSource: "properties.data",
      issues,
      hasGuardedRows: componentData.length > 0,
    };
  }

  if (eventData !== undefined) {
    return {
      rows: eventData,
      rowSource: "properties.events",
      issues,
      hasGuardedRows: eventData.length > 0,
    };
  }

  return {
    rows: [],
    rowSource: "empty",
    issues,
    hasGuardedRows: false,
  };
}

function normalizeError(
  error: ComponentDataResolverRuntimeState["error"],
): ComponentDataResolverError | null {
  if (error === undefined || error === null) {
    return null;
  }

  if (typeof error === "string") {
    return {
      code: "invalid-runtime-rows",
      message: error,
    };
  }

  return error;
}

function isSourceLevelCode(
  code:
    | ComponentDataResolverError["code"]
    | ComponentDataResolverReconnectState["code"]
    | undefined,
): boolean {
  return (
    code === "missing-source-scope" ||
    code === "source-inaccessible" ||
    code === "source-owner-scope-mismatch" ||
    code === "source-permission-denied" ||
    code === "source-write-authority-required" ||
    code === "source-unavailable"
  );
}

function isSourceLevelError(
  error: ComponentDataResolverError | null,
  reconnect: ComponentDataResolverReconnectState | null,
): boolean {
  return isSourceLevelCode(error?.code) || isSourceLevelCode(reconnect?.code);
}

export function mapCatalogSourceErrorToResolverState(
  code: CatalogSourceReconnectReason,
  message: string,
): {
  error: ComponentDataResolverError;
  reconnect: ComponentDataResolverReconnectState;
} {
  if (code === "missing-source-scope") {
    return {
      error: {
        code,
        message,
      },
      reconnect: {
        code: "manual-review-required",
        message,
      },
    };
  }

  return {
    error: {
      code,
      message,
    },
    reconnect: {
      code,
      message,
    },
  };
}

function getCatalogSourceReconnectReason(
  value: unknown,
): CatalogSourceReconnectReason | null {
  if (
    value === "missing-source-scope" ||
    value === "source-inaccessible" ||
    value === "source-owner-scope-mismatch" ||
    value === "source-permission-denied" ||
    value === "source-write-authority-required" ||
    value === "source-unavailable"
  ) {
    return value;
  }

  return null;
}

function getErrorDetailsRecord(error: unknown): Record<string, unknown> | null {
  if (!isRecord(error)) {
    return null;
  }

  const details = error.details;
  if (isRecord(details)) {
    return details;
  }

  const response = error.response;
  if (!isRecord(response)) {
    return null;
  }

  const data = response.data;
  if (!isRecord(data) || !isRecord(data.details)) {
    return null;
  }

  return data.details;
}

export function getCatalogSourceReasonFromError(
  error: unknown,
): CatalogSourceReconnectReason | null {
  const details = getErrorDetailsRecord(error);
  if (details === null) {
    return null;
  }

  const reconnectReason = getCatalogSourceReconnectReason(
    details.reconnectReason,
  );
  if (reconnectReason !== null) {
    return reconnectReason;
  }

  const diagnostics = details.diagnostics;
  if (!isRecord(diagnostics)) {
    return null;
  }

  return getCatalogSourceReconnectReason(diagnostics.reconnectReason);
}

export function createRuntimeStateFromQueryTemplateError(
  error: unknown,
  fallbackMessage: string,
): Pick<ComponentDataResolverRuntimeState, "error" | "reconnect"> {
  const sourceReason = getCatalogSourceReasonFromError(error);
  const message =
    error instanceof Error && error.message.trim().length > 0
      ? error.message
      : fallbackMessage;

  if (sourceReason !== null) {
    return mapCatalogSourceErrorToResolverState(sourceReason, message);
  }

  return {
    error: {
      code: "invalid-runtime-rows",
      message,
    },
    reconnect: null,
  };
}

function getRuntimeRows(state: ComponentDataResolverRuntimeState | undefined): {
  rows: Record<string, unknown>[] | undefined;
  issue: ComponentDataResolverIssue | null;
} {
  if (state?.rows === undefined) {
    return {
      rows: undefined,
      issue: null,
    };
  }

  const rows = getRecordArray(state.rows);
  if (rows !== undefined) {
    return {
      rows,
      issue: null,
    };
  }

  return {
    rows: undefined,
    issue: {
      code: "invalid-runtime-rows",
      message:
        "Runtime resolver rows were ignored because they are not an array of records.",
    },
  };
}

function getBindingSourceKind(
  source: ComponentDataBindingSource,
): ComponentDataResolverSourceKind {
  if (source.kind === "static") {
    return "binding-static";
  }

  if (source.kind === "schema") {
    return "binding-schema";
  }

  if (source.kind === "query-template") {
    return "binding-query-template";
  }

  if (source.kind === "saved-view") {
    return "binding-saved-view";
  }

  return "binding-federated-view";
}

function createSourceMetadata(
  kind: ComponentDataResolverSourceKind,
  rowSource: ComponentDataResolverRowSource,
  binding: ComponentDataBinding | null,
  legacyDataSource: ChartDataSource | null,
  legacyQueryTemplateId: string | null,
  legacyQueryTemplateOrganizationId: string | null,
  issues: ComponentDataResolverIssue[],
  sourceErrors: FederatedViewSourceError[] = [],
): ComponentDataResolverSourceMetadata {
  return {
    kind,
    rowSource,
    binding,
    bindingSource: binding?.source ?? null,
    legacyDataSource,
    legacyQueryTemplateId,
    legacyQueryTemplateOrganizationId,
    sourceErrors,
    issues,
  };
}

function createDiagnostics(
  issues: ComponentDataResolverIssue[],
): ComponentDataResolverDiagnostics {
  return { issues };
}

function createWriteTargetMetadata(
  binding: ComponentDataBinding | null,
  state: ComponentDataResolverWriteTargetRuntimeState | undefined,
): ComponentDataResolverWriteTargetMetadata {
  const writeTarget = binding?.writeTarget ?? null;
  const source =
    writeTarget?.kind === "federated-view-source"
      ? writeTarget.source
      : writeTarget;

  if (writeTarget === null) {
    if (binding !== null && binding.source.kind !== "static") {
      return {
        writeTarget: null,
        source: null,
        requestingOrganizationId: null,
        operations: NO_WRITE_TARGET_PERMISSIONS,
        loading: false,
        error: {
          code: "source-write-authority-required",
          message: SELECT_WRITE_TARGET_MESSAGE,
        },
        reconnect: {
          code: "select-target",
          message: SELECT_WRITE_TARGET_MESSAGE,
        },
      };
    }

    return {
      writeTarget: null,
      source: null,
      requestingOrganizationId: null,
      operations: NO_WRITE_TARGET_PERMISSIONS,
      loading: false,
      error: null,
      reconnect: null,
    };
  }

  if (state === undefined) {
    return {
      writeTarget,
      source,
      requestingOrganizationId: null,
      operations: NO_WRITE_TARGET_PERMISSIONS,
      loading: false,
      error: {
        code: "source-write-authority-required",
        message: RESOLVE_WRITE_AUTHORITY_MESSAGE,
      },
      reconnect: {
        code: "source-write-authority-required",
        message: RESOLVE_WRITE_AUTHORITY_MESSAGE,
      },
    };
  }

  return {
    writeTarget,
    source,
    requestingOrganizationId: state.requestingOrganizationId ?? null,
    operations: {
      create: state.operations?.create === true,
      update: state.operations?.update === true,
      delete: state.operations?.delete === true,
    },
    loading: state.loading ?? false,
    error: normalizeError(state.error),
    reconnect: state.reconnect ?? null,
  };
}

export function resolveComponentData(
  component: Component,
  options: ComponentDataResolverOptions = {},
): ComponentDataResolverResult {
  const refetch = options.refetch ?? defaultRefetch;
  const legacyDataSource = getLegacyDataSource(component);
  const legacyQueryTemplateId = getLegacyQueryTemplateId(component);
  const legacyQueryTemplateOrganizationId =
    getPersistedLegacyQueryOrganizationId(component);
  const legacyStatic = getLegacyStaticResolution(component.properties);
  const bindingCandidate = component.properties.dataBinding;
  const hasBindingCandidate = hasOwnProperty(
    component.properties,
    "dataBinding",
  );
  const binding = parseComponentDataBinding(bindingCandidate);

  if (hasBindingCandidate && binding === undefined) {
    const invalidBindingIssue: ComponentDataResolverIssue = {
      code: "invalid-binding",
      message: "Component dataBinding is malformed and requires manual review.",
    };
    const reconnect = {
      code: "manual-review-required",
      message: invalidBindingIssue.message,
    } satisfies ComponentDataResolverReconnectState;
    const issues = [invalidBindingIssue, ...legacyStatic.issues];

    return {
      rows: [],
      loading: false,
      error: invalidBindingIssue,
      reconnect,
      writeTarget: createWriteTargetMetadata(null, undefined),
      source: createSourceMetadata(
        "empty",
        "empty",
        null,
        legacyDataSource,
        legacyQueryTemplateId,
        legacyQueryTemplateOrganizationId,
        issues,
      ),
      diagnostics: createDiagnostics(issues),
      refetch,
    };
  }

  if (binding !== undefined) {
    const bindingRuntime = getRuntimeRows(options.bindingState);
    const issues = [...legacyStatic.issues];
    if (bindingRuntime.issue) {
      issues.push(bindingRuntime.issue);
    }

    const hasLegacyConflict =
      legacyStatic.hasGuardedRows ||
      legacyDataSource !== null ||
      legacyQueryTemplateId !== null;
    const isSchemaBinding = binding.source.kind === "schema";

    let reconnect = options.bindingState?.reconnect ?? null;
    let error = normalizeError(options.bindingState?.error);
    const hasBindingSourceLevelError = isSourceLevelError(error, reconnect);
    if (hasLegacyConflict && !hasBindingSourceLevelError && !isSchemaBinding) {
      const conflictIssue: ComponentDataResolverIssue = {
        code: "binding-manual-review-required",
        message:
          "Valid dataBinding is authoritative; legacy durable data is present and requires manual review.",
      };
      issues.push(conflictIssue);
      reconnect = {
        code: "manual-review-required",
        message: conflictIssue.message,
      };
      error = conflictIssue;
    }

    const preserveSourceErrorRows =
      hasBindingSourceLevelError &&
      options.bindingState?.preserveRowsOnSourceError === true &&
      bindingRuntime.rows !== undefined;
    const suppressRows =
      (!isSchemaBinding && hasLegacyConflict && !hasBindingSourceLevelError) ||
      (hasBindingSourceLevelError && !preserveSourceErrorRows);
    const bindingRefetch = options.bindingState?.refetch ?? refetch;
    return {
      rows: suppressRows ? [] : (bindingRuntime.rows ?? []),
      loading: options.bindingState?.loading ?? false,
      error,
      reconnect,
      writeTarget: createWriteTargetMetadata(binding, options.writeTargetState),
      source: createSourceMetadata(
        getBindingSourceKind(binding.source),
        "binding",
        binding,
        legacyDataSource,
        legacyQueryTemplateId,
        legacyQueryTemplateOrganizationId,
        issues,
        options.bindingState?.sourceErrors,
      ),
      diagnostics: createDiagnostics(issues),
      refetch: bindingRefetch,
    };
  }

  const legacyQueryActive = isLegacyQueryTemplateActive(
    legacyDataSource,
    legacyQueryTemplateId,
  );
  if (legacyQueryActive) {
    const queryRuntime = getRuntimeRows(options.legacyQueryTemplateState);
    const issues = [...legacyStatic.issues];
    if (queryRuntime.issue) {
      issues.push(queryRuntime.issue);
    }

    const runtimeError = normalizeError(
      options.legacyQueryTemplateState?.error,
    );
    const runtimeReconnect =
      options.legacyQueryTemplateState?.reconnect ?? null;
    if (legacyQueryTemplateOrganizationId === null) {
      const missingSourceIssue: ComponentDataResolverIssue = {
        code: "missing-legacy-query-template-source",
        message:
          "Legacy query-template source is missing persisted organization scope and requires reconnection.",
      };
      const reconnect = {
        code: "manual-review-required",
        message: missingSourceIssue.message,
      } satisfies ComponentDataResolverReconnectState;

      return {
        rows: [],
        loading: options.legacyQueryTemplateState?.loading ?? false,
        error: {
          code: "missing-source-scope",
          message: missingSourceIssue.message,
        },
        reconnect,
        writeTarget: createWriteTargetMetadata(null, undefined),
        source: createSourceMetadata(
          "legacy-query-template",
          "empty",
          null,
          legacyDataSource,
          legacyQueryTemplateId,
          legacyQueryTemplateOrganizationId,
          [missingSourceIssue, ...issues],
        ),
        diagnostics: createDiagnostics([missingSourceIssue, ...issues]),
        refetch: options.legacyQueryTemplateState?.refetch ?? refetch,
      };
    }

    const hasSourceLevelError = isSourceLevelError(
      runtimeError,
      runtimeReconnect,
    );
    const rows = hasSourceLevelError
      ? []
      : (queryRuntime.rows ?? legacyStatic.rows);
    const rowSource =
      queryRuntime.rows && !hasSourceLevelError
        ? "legacy-query-template"
        : hasSourceLevelError
          ? "empty"
          : legacyStatic.rowSource;
    const queryRefetch = options.legacyQueryTemplateState?.refetch ?? refetch;

    return {
      rows,
      loading: options.legacyQueryTemplateState?.loading ?? false,
      error: runtimeError,
      reconnect: runtimeReconnect,
      writeTarget: createWriteTargetMetadata(null, undefined),
      source: createSourceMetadata(
        "legacy-query-template",
        rowSource,
        null,
        legacyDataSource,
        legacyQueryTemplateId,
        legacyQueryTemplateOrganizationId,
        issues,
      ),
      diagnostics: createDiagnostics(issues),
      refetch: queryRefetch,
    };
  }

  return {
    rows: legacyStatic.rows,
    loading: false,
    error: null,
    reconnect: null,
    writeTarget: createWriteTargetMetadata(null, undefined),
    source: createSourceMetadata(
      legacyDataSource !== null ? "legacy-data-source" : "legacy-static",
      legacyStatic.rowSource,
      null,
      legacyDataSource,
      legacyQueryTemplateId,
      legacyQueryTemplateOrganizationId,
      legacyStatic.issues,
    ),
    diagnostics: createDiagnostics(legacyStatic.issues),
    refetch,
  };
}
